import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, Observable } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { MakeProvider } from '../../../../helpers/base-control-value-accessor';
import { MultiValueFormInput } from '../../../../helpers/multi-value-form-input';
import { GetDisabledMsgPipe } from '../../../../pipes/disabled-message.pipe';
import { allowedRadioListFormats } from '../../../entities/allowed-radio-list-formvalues.entity';
import { FormRadioListItem } from '../../../entities/form-radio-list-item.entity';
import { FormValue, FormValueState } from '../../../entities/form-value.entity';

@Component({
    selector: 'sh-form-radio-list-complex-base',
    templateUrl: './form-radio-list-complex-base.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(ShFormRadioListComplexBaseComponent)],
    standalone: true,
    imports: [NgIf, NgClass, NgFor, ReactiveFormsModule, AsyncPipe, GetDisabledMsgPipe]
})
export class ShFormRadioListComplexBaseComponent
    extends MultiValueFormInput<allowedRadioListFormats | null>
    implements OnInit
{
    public control: UntypedFormControl;
    public state$: BehaviorSubject<FormValue<allowedRadioListFormats>[]> = new BehaviorSubject<
        FormValue<allowedRadioListFormats>[]
    >([]);

    @Input() inline = false;
    @Input() groupName!: string;
    @Input() items: FormRadioListItem[] = [];
    @Input() invalid = false;
    @Input() describeLabelId!: string;
    @Input() id!: string;
    @Input() disabledMsg?: string;

    constructor(changeDetectorRef: ChangeDetectorRef) {
        super(changeDetectorRef);
        this.control = new UntypedFormControl();
    }

    ngOnInit(): void {
        this.initListeners();
    }

    private initListeners(): void {
        const input$: Observable<allowedRadioListFormats> = this.control.valueChanges;

        const validatedInput$: Observable<allowedRadioListFormats> = input$.pipe(
            filter(() => this.items !== null),
            filter(value => this.items.map(item => item.value).includes(value))
        );

        const firstValue$: Observable<allowedRadioListFormats> = validatedInput$.pipe(
            filter(value => value !== null && typeof value !== 'undefined'),
            take(1)
        );

        validatedInput$.pipe(takeUntil(this.destroy$)).subscribe(value => {
            const setFormValue: FormValue<allowedRadioListFormats> = new FormValue(value, FormValueState.SET);
            this.state$.next([setFormValue]);
            this.propagateChange([setFormValue]);
        });
    }

    public writeValueImpl(input: FormValue<any>[]): void {
        /*  superclass ensures that it's an FormValue<any>[] */
        /* First we filter out null values */
        const obj: FormValue<any>[] = input.filter(element => element !== null && element.value !== null);

        /* Then we need to check that value is string */
        if (!obj.every(formValue => typeof formValue.value === 'string' || typeof formValue.value === 'boolean')) {
            console.error(
                'ShFormRadioListComplexBaseComponent:: value must be Array<FormValue<string | boolean>>',
                obj
            );
            return this.writeValueImpl([]);
        }

        this.state$.next(input);

        const setFormValue: FormValue<string | boolean> | undefined = obj.find(
            formValue => formValue.state === FormValueState.SET
        );
        if (setFormValue) {
            this.control.setValue(setFormValue.value, { emitEvent: false });
        } else if (input.length === 1 && input[0].state === FormValueState.UNSET && !input[0].value) {
            this.control.setValue(null); // Clear command received
        }
    }

    public isIndeterminate(value: string): boolean {
        const formValue: FormValue<allowedRadioListFormats> | undefined = this.state$.value.find(
            fv => fv.value === value
        );
        if (typeof formValue === 'undefined') {
            return false;
        }
        return formValue.state === FormValueState.INDETERMINATE;
    }
}
